import { HttpRequest, HttpResponseJson } from '../../utils/http/Http';
import { AccountType } from '../Constants';
import {
	AccountConfigReadyMessage,
	AddSelectedAccountMessage,
	CloseAddAccountViewMessage,
	ContinueMigrationMessage,
	DisplayCertificateMessage,
	DoTryAgainMessage,
	FetchMessage,
	GetAccountsMessage,
	GetEmailDiscoveredUrlUsingDNSandDHCPMessage,
	GetEmailDiscoveredUrlUsingDNSandDHCPParams,
	IsSwitchViewAllowedOnAuthUIMessage,
	OnAddAccountWithoutUICompleteMessage,
	OnGetCapabilitiesCompleteMessage,
	OnRefreshCompleteMessage,
	OnWebUIReadyToLoadMessage,
	OpenLinkMessage,
	PerformanceMeasureMessage,
	SendActionMessage,
	SetAddressDiscoveredStoreUrlsMessage,
	ShouldAddAccountProceedMessage,
	ShowAccountsMessage,
	SkipMigrationMessage,
	StartCloudStoreFlowMessage,
	StartCustomPortalFlowMessage,
	StartCustomPortalFlowParams,
	StartLegacyStoreFlowMessage,
	StartOnPremStoreFlowMessage,
	SwitchNativeWebViewMessage,
	ValidateConfigSettingsMessage,
	ValidatePublicCertChainMessage,
	ValidateCertChainAndPayloadMessage,
	ValidateCertChainAndPayloadParams,
	GetAppConfigDiscoveryDocumentMessage,
	GetAppConfigDiscoveryDocumentParams,
	GetAppConfigDiscoveryDocumentResponse,
	WriteTraceMessage,
	SetAddressDiscoveredStoreUrlsV2Params,
	SetAddressDiscoveredStoreUrlsV2Message,
	StartCloudStoreFlowParams,
	StartLegacyFlowParams,
	OnPerformGACSDiscoveryCompleteMessgae,
	AddSelectedAccountParams,
	FetchGACSSettingsV2Params,
	FetchGACSSettingsV2Message,
	GACSHybridStoreInfo,
	GetAllGACSHybridStoresInfoMessage,
	OnGetNetworkLocationFromURLCompleteMessage,
} from './NativeInterface';

import { NativeBridgeBase } from './nativeBridgeBase';
import {
	DisplayCertificateParams,
	OpenLinkParms,
	SetAddressDiscoveredStoreUrlsParams,
	ShouldAddAccountProceedParams,
	StartOnPremFlowParams,
	SwitchNativeWebViewParams,
} from './NativeInterface';
import { NativeAccount } from '../core/TypeDefinitions';
import { ResponseJSON } from 'src/Common/BridgeInterface';
import { GACSSettingEndPoint } from '../core/AppConfigService/AppConfigService';
import { AotCategoryType } from '../../utils/Constants';

export class NativeBridgeWrapper extends NativeBridgeBase {
	private static _instance: NativeBridgeWrapper;
	private constructor() {
		super();
	}

	static get Instance(): NativeBridgeWrapper {
		return (
			NativeBridgeWrapper._instance ||
			(NativeBridgeWrapper._instance = new NativeBridgeWrapper())
		);
	}

	// Type:call bridge functions
	// These functions are invoked by JS and Native returns it's response as callback
	async fetch(request: HttpRequest): Promise<HttpResponseJson> {
		return new Promise((resolve, reject) => {
			const id = this.addRequestToQueue(resolve, reject);
			const message: FetchMessage = {
				type: 'call',
				id,
				name: 'fetch',
				params: request.toJSON(),
			};
			window.messageToNative(JSON.stringify(message));
		});
	}

	async getAccounts(): Promise<{ accounts: NativeAccount[] }> {
		return new Promise((resolve, reject) => {
			const id = this.addRequestToQueue(resolve, reject);
			const message: GetAccountsMessage = {
				type: 'call',
				id,
				name: 'getAccounts',
				params: {},
			};
			window.messageToNative(JSON.stringify(message));
		});
	}

	async getEmailDiscoveredUrlUsingDNSandDHCP(
		params: GetEmailDiscoveredUrlUsingDNSandDHCPParams
	): Promise<{ discoveredUrl: string }> {
		return new Promise((resolve, reject) => {
			const id = this.addRequestToQueue(resolve, reject);
			const message: GetEmailDiscoveredUrlUsingDNSandDHCPMessage = {
				type: 'call',
				id,
				name: 'getEmailDiscoveredUrlUsingDNSandDHCP',
				params,
			};
			window.messageToNative(JSON.stringify(message));
		});
	}
	//Type:callback bridge functions
	//These function return response to the function call started by native
	onRefreshComplete(id: string, payload: ResponseJSON): void {
		const message: OnRefreshCompleteMessage = {
			type: 'callback',
			id,
			params: payload,
		};
		window.messageToNative(JSON.stringify(message));
	}

	onAddAccountWithoutUIComplete(id: string, payload: ResponseJSON): void {
		const message: OnAddAccountWithoutUICompleteMessage = {
			type: 'callback',
			id,
			params: payload,
		};
		window.messageToNative(JSON.stringify(message));
	}

	onGetNetworkLocationFromURLComplete(id: string, payload: ResponseJSON): void {
		const message: OnGetNetworkLocationFromURLCompleteMessage = {
			type: 'callback',
			id,
			params: payload,
		};
		window.messageToNative(JSON.stringify(message));
	}

	onGetCapabilitiesComplete(id: string, payload: ResponseJSON): void {
		const message: OnGetCapabilitiesCompleteMessage = {
			type: 'callback',
			id,
			params: payload,
		};
		window.messageToNative(JSON.stringify(message));
	}

	//Type:event bridge functions
	//These are fire and forget kind of functions which indicate native that something happened on JS
	accountConfigReady(): void {
		const message: AccountConfigReadyMessage = {
			type: 'event',
			name: 'accountConfigReady',
			params: {},
		};
		window.messageToNative(JSON.stringify(message));
	}

	closeAddAccountView(): void {
		const message: CloseAddAccountViewMessage = {
			type: 'event',
			name: 'closeAddAccountView',
			params: {},
		};
		window.messageToNative(JSON.stringify(message));
	}

	doTryAgain(): void {
		const message: DoTryAgainMessage = {
			type: 'event',
			name: 'doTryAgain',
			params: {},
		};
		window.messageToNative(JSON.stringify(message));
	}

	onWebUIReadyToLoad(webUI: string, webUIAuthentication: string): void {
		const message: OnWebUIReadyToLoadMessage = {
			type: 'event',
			name: 'onWebUIReadyToLoad',
			params: {
				webUI,
				webUIAuthentication,
			},
		};
		window.messageToNative(JSON.stringify(message));
	}

	performanceMeasure(eventName: string, perfTimeStamp: string): void {
		const message: PerformanceMeasureMessage = {
			type: 'event',
			name: 'performanceMeasure',
			params: {
				eventName,
				perfTimeStamp,
			},
		};
		window.messageToNative(JSON.stringify(message));
	}

	sendAction(storeID: string): void {
		const message: SendActionMessage = {
			type: 'event',
			name: 'sendAction',
			params: { storeID },
		};
		window.messageToNative(JSON.stringify(message));
	}

	setAddressDiscoveredStoreUrls(params: SetAddressDiscoveredStoreUrlsParams): void {
		const message: SetAddressDiscoveredStoreUrlsMessage = {
			type: 'event',
			name: 'setAddressDiscoveredStoreUrls',
			params,
		};
		window.messageToNative(JSON.stringify(message));
	}

	setAddressDiscoveredStoreUrlsV2(params: SetAddressDiscoveredStoreUrlsV2Params): void {
		const message: SetAddressDiscoveredStoreUrlsV2Message = {
			type: 'event',
			name: 'setAddressDiscoveredStoreUrlsV2',
			params,
		};
		window.messageToNative(JSON.stringify(message));
	}

	fetchGACSSettingsV2(params: FetchGACSSettingsV2Params): void {
		const message: FetchGACSSettingsV2Message = {
			type: 'event',
			name: 'fetchGACSSettingsV2',
			params,
		};
		window.messageToNative(JSON.stringify(message));
	}

	openLink(params: string): void {
		const { url } = JSON.parse(params) as OpenLinkParms;
		const message: OpenLinkMessage = {
			type: 'event',
			name: 'openLink',
			params: { url },
		};
		window.messageToNative(JSON.stringify(message));
	}

	showAccounts(): void {
		const message: ShowAccountsMessage = {
			type: 'event',
			name: 'showAccounts',
			params: {},
		};
		window.messageToNative(JSON.stringify(message));
	}

	startCloudStoreFlow(params: StartCloudStoreFlowParams): Promise<{}> {
		return new Promise((resolve, reject) => {
			const id = this.addRequestToQueue(resolve, reject);
			const message: StartCloudStoreFlowMessage = {
				type: 'call',
				id,
				name: 'startCloudStoreFlow',
				params,
			};
			window.messageToNative(JSON.stringify(message));
		});
	}

	startOnPremStoreFlow(params: StartOnPremFlowParams): Promise<{}> {
		return new Promise((resolve, reject) => {
			const id = this.addRequestToQueue(resolve, reject);
			const message: StartOnPremStoreFlowMessage = {
				type: 'call',
				id,
				name: 'startOnPremStoreFlow',
				params,
			};
			window.messageToNative(JSON.stringify(message));
		});
	}

	startLegacyStoreFlow(
		params: StartLegacyFlowParams
	): Promise<{ accounts: Partial<NativeAccount>[] }> {
		return new Promise((resolve, reject) => {
			const id = this.addRequestToQueue(resolve, reject);
			const message: StartLegacyStoreFlowMessage = {
				type: 'call',
				id: id,
				name: 'startLegacyStoreFlow',
				params,
			};
			window.messageToNative(JSON.stringify(message));
		});
	}

	shouldAddAccountProceed(params: string): Promise<{ status: boolean }> {
		return new Promise((resolve, reject) => {
			const id = this.addRequestToQueue(resolve, reject);
			const paramsJSON = JSON.parse(params) as ShouldAddAccountProceedParams;
			const message: ShouldAddAccountProceedMessage = {
				type: 'call',
				id,
				name: 'shouldAddAccountProceed',
				params: paramsJSON,
			};
			window.messageToNative(JSON.stringify(message));
		});
	}

	startCustomPortalFlow(params: StartCustomPortalFlowParams): Promise<{}> {
		return new Promise((resolve, reject) => {
			const id = this.addRequestToQueue(resolve, reject);
			const message: StartCustomPortalFlowMessage = {
				type: 'call',
				id: id,
				name: 'startCustomPortalFlow',
				params,
			};
			window.messageToNative(JSON.stringify(message));
		});
	}

	addSelectedAccount(jsonParams: AddSelectedAccountParams): Promise<{}> {
		return new Promise((resolve, reject) => {
			const id = this.addRequestToQueue(resolve, reject);
			const message: AddSelectedAccountMessage = {
				type: 'call',
				id,
				name: 'addSelectedAccount',
				params: jsonParams,
			};
			window.messageToNative(JSON.stringify(message));
		});
	}

	validateConfigSettings(
		type: AccountType,
		address: string,
		storeUrls: string[],
		gacsSettingsEndpoints: Record<string, GACSSettingEndPoint>
	): Promise<{ selectedStoreUrl: string }> {
		return new Promise((resolve, reject) => {
			const id = this.addRequestToQueue(resolve, reject);
			const message: ValidateConfigSettingsMessage = {
				type: 'call',
				id,
				name: 'validateConfigSettings',
				params: {
					address,
					type,
					storeUrls,
					gacsSettingsEndpoints,
				},
			};
			window.messageToNative(JSON.stringify(message));
		});
	}

	validatePublicCertChain(certificate: string): Promise<{}> {
		return new Promise((resolve, reject) => {
			const id = this.addRequestToQueue(resolve, reject);
			const message: ValidatePublicCertChainMessage = {
				type: 'call',
				id,
				name: 'validatePublicCertChain',
				params: { certificate },
			};
			window.messageToNative(JSON.stringify(message));
		});
	}

	validateCertChainAndPayload(
		params: ValidateCertChainAndPayloadParams
	): Promise<{ isValid: boolean }> {
		return new Promise((resolve, reject) => {
			const id = this.addRequestToQueue(resolve, reject);
			const message: ValidateCertChainAndPayloadMessage = {
				type: 'call',
				id,
				name: 'validateCertChainAndPayload',
				params,
			};
			window.messageToNative(JSON.stringify(message));
		});
	}

	getAppConfigDiscoveryDocument(jsonParams: GetAppConfigDiscoveryDocumentParams): Promise<GetAppConfigDiscoveryDocumentResponse> {
		return new Promise((resolve, reject) => {
			const id = this.addRequestToQueue(resolve, reject);
			const message: GetAppConfigDiscoveryDocumentMessage = {
				type: 'call',
				id,
				name: 'getAppConfigDiscoveryDocument',
				params: jsonParams,
			};
			window.messageToNative(JSON.stringify(message));
		});
	}

	writeTrace(
		module: string,
		trace: string,
		level?: string,
		aotCategory?: AotCategoryType
	): void {
		const message: WriteTraceMessage = {
			type: 'event',
			name: 'writeTrace',
			params: { module, level, aotCategory, message: trace },
		};
		window.messageToNative(JSON.stringify(message));
	}

	continueMigration(): void {
		const message: ContinueMigrationMessage = {
			type: 'event',
			name: 'continueMigration',
			params: {},
		};
		window.messageToNative(JSON.stringify(message));
	}

	skipMigration(): void {
		const message: SkipMigrationMessage = {
			type: 'event',
			name: 'skipMigration',
			params: {},
		};
		window.messageToNative(JSON.stringify(message));
	}

	displayCertificate(params: string): void {
		const paramsJSON = JSON.parse(params) as DisplayCertificateParams;
		const message: DisplayCertificateMessage = {
			type: 'event',
			name: 'displayCertificate',
			params: paramsJSON,
		};
		window.messageToNative(JSON.stringify(message));
	}

	switchNativeWebView(paramsStr: string): void {
		const params = JSON.parse(paramsStr) as SwitchNativeWebViewParams;
		const message: SwitchNativeWebViewMessage = {
			type: 'event',
			name: 'switchNativeWebView',
			params,
		};
		window.messageToNative(JSON.stringify(message));
	}

	isSwitchViewAllowedOnAuthUI(): Promise<{ status: boolean }> {
		return new Promise((resolve, reject) => {
			const id = this.addRequestToQueue(resolve, reject);
			const message: IsSwitchViewAllowedOnAuthUIMessage = {
				type: 'call',
				id,
				name: 'isSwitchViewAllowedOnAuthUI',
				params: {},
			};
			window.messageToNative(JSON.stringify(message));
		});
	}

	onPerformGACSDiscoveryComplete(callbackId: string, jsonPayload: ResponseJSON): void {
		const message: OnPerformGACSDiscoveryCompleteMessgae = {
			type: 'callback',
			id: callbackId,
			params: jsonPayload,
		};
		window.messageToNative(JSON.stringify(message));
	}

	getAllGACSHybridStoresInfo(): Promise<{ stores: GACSHybridStoreInfo[] }> {
		return new Promise((resolve, reject) => {
			const id = this.addRequestToQueue(resolve, reject);
			const message: GetAllGACSHybridStoresInfoMessage = {
				type: 'call',
				id,
				name: 'getAllGACSHybridStoresInfo',
				params: {},
			};
			window.messageToNative(JSON.stringify(message));
		});
	}
}
